/* Stockists finder, adapted from the approved prototype.
   Liquid renders public metaobject fields; HTML cards are the only record source.
   Vocabulary is localized separately. No SDK, external script or duplicate record JSON. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!customElements.get('chapon-stockists')) customElements.define('chapon-stockists', class extends HTMLElement {
    connectedCallback() { this.controller = new AbortController(); api.boot(this, this.controller.signal); }
    disconnectedCallback() { this.controller.abort(); this.cleanup?.(); }
  });
})(this, function () {
  'use strict';

  // Retain the pre-i18n Japanese markup, including its punctuation and spacing.
  // Non-Japanese pages supply the complete vocabulary through Liquid.
  var JA_UI = {
    store_unit: '店舗', record_unit: '件', phone: '電話：__name__ ',
    link_context: '：__name__（新しいタブで開く）', more: '残り__count__を表示',
    stores: '実店舗', stores_english: 'Stores', online_english: 'Online',
    jump_type: 'タイプへ移動', jump_area: 'エリアへ移動', prefectures: '__region__の都道府県',
    area: 'エリア', type: '店舗タイプ', keyword: 'キーワード __query__', query: '「__query__」',
    active_filters: '適用中の絞り込み', remove_filter: '__label__の絞り込みを解除', reset: 'すべて解除',
    applied: '（__count__件の条件を適用中）', view_results: '__count__を見る',
    no_results: '条件に合う店舗はありません', found: '__count__が見つかりました',
    map: '地図で見る', detail: '店舗詳細', shop: 'ショップを見る'
  };
  function format(template, values) {
    return template.replace(/__(\w+)__/g, function (_, key) { return String(values[key]); });
  }
  function label(item) { return item.label ?? item.ja; }
  var VIEWS = ['region', 'type', 'name'];
  var ONLINE = 'online'; // pseudo area value for online records (they have no region)
  var CATALOG = 'catalog'; // catalogs have no area and stay separate in every view
  var LIMIT_TOTAL = 48; // beyond this many results, long lists fold behind a "show more" button
  var LIMIT_PREF = 6, LIMIT_FLAT = 12;

  /* ---------- text helpers ---------- */
  // Lookup tables keyed by user-controlled text (URL parameters) must not inherit Object.prototype names.
  function dict() { return Object.create(null); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  // Width/case/kana-insensitive form used for matching only; ヴ spellings match their バ-row variants
  var VU = { 'ゔぁ': 'ば', 'ゔぃ': 'び', 'ゔぇ': 'べ', 'ゔぉ': 'ぼ', 'ゔ': 'ぶ' };
  function norm(s) {
    return String(s == null ? '' : s).normalize('NFKC').toLowerCase()
      .replace(/[ァ-ヶ]/g, function (c) { return String.fromCharCode(c.charCodeAt(0) - 0x60); })
      .replace(/ゔ[ぁぃぇぉ]?/g, function (c) { return VU[c]; })
      .replace(/[\s\-‐‑–—―−ー・〒]/g, '');
  }
  function terms(q) { return String(q || '').normalize('NFKC').split(/\s+/).map(norm).filter(Boolean); }
  function byName(a, b) { return a.name.localeCompare(b.name, 'ja'); }
  // Online shops and catalogs are not physical 店舗; mixed sets are counted in 件.
  function countUnit(list, ui) { ui = ui || JA_UI; return list.some(function (s) { return s.channel === 'online' || s.channel === CATALOG; }) ? ui.record_unit : ui.store_unit; }
  function countText(n, list, ui) { return n + countUnit(list, ui); }

  /* ---------- icons ---------- */
  var ICON = {
    pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
    phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>',
    out: '<path d="M7 7h10v10"/><path d="M7 17 17 7"/>',
    down: '<path d="m6 9 6 6 6-6"/>',
    x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'
  };
  function icon(name) { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + ICON[name] + '</svg>'; }

  /* ---------- environment: lookups built from the model the generator embeds ---------- */
  function makeEnv(model) {
    var env = { model: model, regionBy: dict(), regionByKey: dict(), prefInfo: dict(), typeBy: dict(), onlineLabel: model.onlineLabel, ui: model.ui || JA_UI };
    model.regions.forEach(function (r, i) {
      env.regionBy[r.ja] = r; env.regionByKey[r.key] = r; r._order = i;
      r.prefs.forEach(function (p, j) { env.prefInfo[p.ja] = { kana: p.kana, label: label(p), region: r.ja, order: i * 100 + j }; });
    });
    model.types.forEach(function (t) { env.typeBy[t.key] = t; });
    return env;
  }
  // Search fields, kept apart so that 京都 cannot match inside 東京都:
  //   plain  – matched anywhere (name, facility, city, postal code, type, region)
  //   start  – matched from the first character only (prefecture and its reading)
  //   addr   – matched anywhere except starting inside the leading prefecture name
  function prep(env, stores) {
    stores.forEach(function (s, i) {
      var info = env.prefInfo[s.pref], r = env.regionBy[s.region], t = env.typeBy[s.type], pref = norm(s.pref), addr = norm(s.addr);
      s._o = i;
      var display = s.display || {}, displayAddr = norm(display.addr);
      s._plain = [s.name, s.chain, s.facility, s.city, display.name, display.chain, display.facility, display.city, s.zip, s.region, r ? r.kana : '', env.model.ui && r ? label(r) : '', t ? t.ja : '', s.channel === 'online' ? env.onlineLabel : ''].map(norm).filter(Boolean);
      s._start = [pref, info ? norm(info.kana) : '', env.model.ui && info ? norm(info.label) : ''].filter(Boolean);
      s._addr = addr;
      s._prefLen = pref && addr.indexOf(pref) === 0 ? pref.length : 0;
      s._displayAddr = displayAddr !== addr ? displayAddr : '';
      s._displayPrefLen = pref && displayAddr.indexOf(pref) === 0 ? pref.length : 0;
    });
    return stores;
  }
  function addressMatches(addr, prefLen, term) {
    for (var i = addr.indexOf(term); i >= 0; i = addr.indexOf(term, i + 1)) if (i === 0 || i >= prefLen) return true;
    return false;
  }
  function termMatches(s, t) {
    var i;
    for (i = 0; i < s._plain.length; i++) if (s._plain[i].indexOf(t) >= 0) return true;
    for (i = 0; i < s._start.length; i++) if (s._start[i].indexOf(t) === 0) return true;
    return addressMatches(s._addr, s._prefLen, t) || addressMatches(s._displayAddr, s._displayPrefLen, t);
  }
  // The type label on a card only says something when the page has more than one type in that channel.
  function typeLabelFlags(stores) {
    var seen = { physical: {}, online: {}, catalog: {} };
    stores.forEach(function (s) { seen[s.channel][s.type] = true; });
    return { physical: Object.keys(seen.physical).length > 1, online: Object.keys(seen.online).length > 1, catalog: false };
  }
  // Preserve the physical-type choice rule; catalogs also make their type selectable.
  function hasTypeChoice(stores) {
    var seen = dict(), n = 0, catalogs = stores.some(function (s) { return s.channel === CATALOG; });
    stores.forEach(function (s) { if ((s.channel === 'physical' || catalogs) && !seen[s.type]) { seen[s.type] = true; n++; } });
    return n > 1;
  }

  /* ---------- pure renderer (also run at build time for the static baseline) ---------- */
  // Keep a street number such as 2-16-1 on one line.
  function addressHTML(addr) { return esc(addr).replace(/\d+(?:-\d+)+/g, '<span class="st-nb">$&</span>'); }

  function cardHTML(env, s, level, ctx) {
    var h = 'h' + level, t = env.typeBy[s.type], online = s.channel === 'online', catalog = s.channel === CATALOG, remote = online || catalog, ui = env.ui;
    var display = s.display || {}, name = display.name ?? s.name, chain = display.chain ?? s.chain, address = display.addr ?? s.addr;
    var nameHTML = chain && name.indexOf(chain + ' ') === 0
      ? '<span class="st-card__chain">' + esc(chain) + '</span> ' + esc(name.slice(chain.length + 1))
      : esc(name);
    var pill = t && String(t.ja || '').trim() && ctx.typeLabels[s.channel] && ctx.view !== 'type' ? '<p class="st-card__type"><span>' + esc(t.ja) + '</span></p>' : '';
    var addr = !catalog && address ? '<p class="st-card__addr">' + (s.zip ? '<span class="st-card__zip">〒' + esc(s.zip) + '</span> ' : '') + addressHTML(address) + '</p>' : '';
    var tel = '';
    if (!catalog && s.phone) {
      tel = '<p class="st-card__tel"><a href="tel:' + esc(s.phone.replace(/-/g, '')) + '">' + icon('phone') + '<span class="visually-hidden">' + esc(format(ui.phone, { name: name })) + '</span>' + esc(s.phone) + '</a></p>';
    }
    function link(href, cls, label, ic) {
      var vh = '<span class="visually-hidden">' + esc(format(ui.link_context, { name: name })) + '</span>';
      return '<a class="btn btn--small' + cls + '" href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">' + (ic === 'pin' ? icon('pin') + label + vh : label + vh + icon('out')) + '</a>';
    }
    var acts = '';
    if (remote) { if (s.detail) acts = link(s.detail, ' btn--primary', esc(ui.shop), 'out'); }
    else {
      if (s.map) acts += link(s.map, ' btn--primary', esc(ui.map), 'pin');
      if (s.detail) acts += link(s.detail, '', esc(ui.detail), 'out');
    }
    return '<li class="st-card' + (remote ? ' st-card--online' : '') + '" data-id="' + esc(s.id) + '">' + pill +
      '<' + h + ' class="st-card__name" tabindex="-1">' + nameHTML + '</' + h + '>' +
      addr + tel + (acts ? '<div class="st-card__acts">' + acts + '</div>' : '') + '</li>';
  }

  function listHTML(env, key, items, level, limit, ctx) {
    var shown = items, more = '';
    if (ctx.limited && items.length > limit && !ctx.expanded[key]) {
      shown = items.slice(0, limit);
      more = '<button type="button" class="st-more" data-more="' + esc(key) + '">' + esc(format(env.ui.more, { count: countText(items.length - limit, items, env.ui) })) + icon('down') + '</button>';
    }
    var html = shown.map(function (s, i) {
      var c = cardHTML(env, s, level, ctx);
      return ctx.reveal === key && i === limit ? c.replace('<li class="st-card', '<li data-reveal class="st-card') : c;
    }).join('');
    return '<ul class="st-grid">' + html + '</ul>' + more;
  }

  function headHTML(key, ja, en, n, unit) {
    return '<header class="st-group__head"><h3 class="st-group__h" id="st-h-' + key + '" tabindex="-1">' +
      (en ? '<span class="st-group__en" aria-hidden="true">' + esc(en) + '</span>' : '') + '<span class="st-group__ja">' + esc(ja) + '</span></h3>' +
      '<span class="st-group__rule" aria-hidden="true"></span><span class="st-group__n">' + n + '<small>' + unit + '</small></span></header>';
  }
  function groupOpen(key, extra) { return '<section class="st-group' + (extra || '') + '" id="st-g-' + key + '" aria-labelledby="st-h-' + key + '" data-group="' + key + '">'; }

  // -> { html, groups: [{ key, ja, n, unit }] }. Online and catalog groups follow physical stores.
  function renderResults(env, stores, view, ctx) {
    ctx = ctx || {}; ctx.expanded = ctx.expanded || {}; ctx.view = view; ctx.typeLabels = ctx.typeLabels || { physical: false, online: false };
    var items = stores.slice();
    if (view === 'name') items.sort(byName); else items.sort(function (a, b) { return a._o - b._o; });
    var physical = items.filter(function (s) { return s.channel === 'physical'; });
    var online = items.filter(function (s) { return s.channel === 'online'; });
    var catalogs = items.filter(function (s) { return s.channel === CATALOG; });
    var out = '', groups = [], ui = env.ui;
    if (view === 'type') {
      env.model.types.forEach(function (t) {
        var g = physical.filter(function (s) { return s.type === t.key; });
        if (!g.length) return;
        groups.push({ key: t.key, ja: t.ja, n: g.length, unit: ui.store_unit });
        out += groupOpen(t.key) + headHTML(t.key, t.ja, '', g.length, esc(ui.store_unit)) + listHTML(env, 'type:' + t.key, g, 4, LIMIT_FLAT, ctx) + '</section>';
      });
    } else if (view === 'name') {
      if (physical.length) {
        groups.push({ key: 'stores', ja: ui.stores, n: physical.length, unit: ui.store_unit });
        out += groupOpen('stores') + headHTML('stores', ui.stores, env.model.ui ? '' : ui.stores_english, physical.length, esc(ui.store_unit)) + listHTML(env, 'all', physical, 4, LIMIT_FLAT, ctx) + '</section>';
      }
    } else {
      env.model.regions.forEach(function (r) {
        var g = physical.filter(function (s) { return s.region === r.ja; });
        if (!g.length) return;
        groups.push({ key: r.key, ja: label(r), n: g.length, unit: ui.store_unit });
        out += groupOpen(r.key) + headHTML(r.key, label(r), env.model.ui ? '' : r.en, g.length, esc(ui.store_unit));
        r.prefs.forEach(function (p) {
          var pi = g.filter(function (s) { return s.pref === p.ja; });
          if (!pi.length) return;
          // 北海道 is both region and prefecture: its heading is for assistive technology only, so heading levels stay the same in every region
          var sameName = p.ja === r.ja;
          out += '<div class="st-pref"><h4 class="st-pref__name' + (sameName ? ' visually-hidden' : '') + '">' + esc(label(p)) + '<span>' + pi.length + esc(ui.store_unit) + '</span></h4>' + listHTML(env, 'pref:' + p.ja, pi, 5, LIMIT_PREF, ctx) + '</div>';
        });
        out += '</section>';
      });
    }
    if (online.length) {
      groups.push({ key: ONLINE, ja: env.onlineLabel, n: online.length, unit: ui.record_unit });
      out += groupOpen(ONLINE, ' st-group--online') + headHTML(ONLINE, env.model.onlineHeading, env.model.ui ? '' : ui.online_english, online.length, esc(ui.record_unit)) + listHTML(env, 'online', online, 4, LIMIT_FLAT, ctx) + '</section>';
    }
    if (catalogs.length) {
      groups.push({ key: CATALOG, ja: env.model.catalogHeading, n: catalogs.length, unit: ui.record_unit });
      out += groupOpen(CATALOG, ' st-group--online') + headHTML(CATALOG, env.model.catalogHeading, '', catalogs.length, esc(ui.record_unit)) + listHTML(env, CATALOG, catalogs, 4, LIMIT_FLAT, ctx) + '</section>';
    }
    return { html: out, groups: groups };
  }

  // Jump links between groups: only when the list is long enough to need them. Plain anchors, so they work without JS.
  function jumpHTML(view, groups, total, ui) {
    if (groups.length < 3 || total < 12) return '';
    ui = ui || JA_UI;
    var label = view === 'type' ? ui.jump_type : ui.jump_area;
    return '<nav class="st-jump" aria-label="' + esc(label) + '"><p class="st-jump__t" aria-hidden="true">' + esc(label) + '</p><ul>' + groups.map(function (g) {
      return '<li><a href="#st-g-' + g.key + '" aria-label="' + esc(g.ja) + ' ' + g.n + g.unit + '">' + esc(g.ja) + ' <em aria-hidden="true">' + g.n + '</em></a></li>';
    }).join('') + '</ul></nav>';
  }

  /* ==========================================================================
     Browser behaviour
     ========================================================================== */
  async function boot(host, signal) {
    function on(target, event, handler) { target.addEventListener(event, handler, { signal: signal }); }
    var suffix = '-' + host.dataset.sectionId;
    function selector(s) { return s.replace(/#([\w-]+)/g, function (_, id) { return '#' + id + suffix; }); }
    function uniqueMarkup(s) { return s.replace(/(id|aria-controls|aria-labelledby)="([^"]+)"/g, function (_, attr, id) { return attr + '="' + id + suffix + '"'; }).replace(/href="#([^"]+)"/g, function (_, id) { return 'href="#' + id + suffix + '"'; }); }
    function record(card) {
      var address = card.querySelector('[data-address]');
      return { id: card.dataset.id, name: card.dataset.name, chain: card.dataset.chain, type: card.dataset.storeType,
        channel: card.dataset.channel, region: card.dataset.region, pref: card.dataset.prefecture, city: card.dataset.city,
        facility: card.dataset.facility, zip: card.querySelector('.st-card__zip')?.textContent || '',
        addr: address?.dataset.address || '', phone: card.querySelector('.st-card__tel a')?.textContent || '',
        display: { name: card.dataset.displayName, chain: card.dataset.displayChain, city: card.dataset.displayCity,
          facility: card.dataset.displayFacility, addr: address?.dataset.displayAddress },
        map: card.querySelector('[data-map]')?.getAttribute('href') || '', detail: card.querySelector('[data-detail]')?.getAttribute('href') || '',
        sort: Number(card.dataset.sort) || 0 };
    }
    var cards = Array.from(host.querySelectorAll('[data-store-card]'));
    var next = host.querySelector('[data-pages] a[rel="next"]');
    var pageInfo = host.querySelector('[data-page-info]');
    if (Number(pageInfo.dataset.currentPage) > 1) {
      // A direct paginated URL must still build a complete, not partial, search index.
      var firstURL = new URL(location.href);
      firstURL.searchParams.set(pageInfo.dataset.pageParam, '1');
      next = document.createElement('a'); next.href = firstURL.href;
      cards = [];
    }
    var seen = new Set(cards.map(function(c) { return c.dataset.id; }));
    var pagesSeen = new Set();
    try {
      while (next) {
        if (cards.length >= 1000 || pagesSeen.size >= 4) throw new Error('Stockists exceeds the local HTML index limit');
        var pageURL = new URL(next.getAttribute('href'), location.href);
        // Preserve merchant preview and alternate-template parameters across pages.
        var context = new URL(location.href);
        context.searchParams.forEach(function(value, key) { if (!pageURL.searchParams.has(key)) pageURL.searchParams.set(key, value); });
        pageURL.searchParams.set('section_id', host.dataset.sectionId);
        if (pagesSeen.has(pageURL.href)) throw new Error('Stockists pagination repeated a page');
        pagesSeen.add(pageURL.href);
        var response = await fetch(pageURL, { signal: signal });
        if (!response.ok) throw new Error('Stockists page load: ' + response.status);
        var fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!fragment.querySelector('chapon-stockists')) throw new Error('Stockists section returned no index');
        var incoming = Array.from(fragment.querySelectorAll('[data-store-card]'));
        var countBefore = cards.length;
        incoming.forEach(function(card) { if (!seen.has(card.dataset.id)) { seen.add(card.dataset.id); cards.push(card); } });
        if (incoming.length && cards.length === countBefore) throw new Error('Stockists pagination returned no new entries');
        next = fragment.querySelector('[data-pages] a[rel="next"]');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error); host.querySelector('[data-load-error]').hidden = false; return;
    }
    if (signal.aborted || !host.isConnected) return;

    var $ = function (s, el) { return (el || host).querySelector(selector(s)); };
    var $$ = function (s, el) { return Array.prototype.slice.call((el || host).querySelectorAll(selector(s))); };
    var doc = document.documentElement;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var coarse = window.matchMedia('(pointer: coarse)');
    var finder = host;
    if (!finder) return;
    var el = {
      input: $('#st-q'), clearQ: $('[data-q-clear]'), quick: $('[data-quick]'), results: $('#st-results'), empty: $('#st-empty'), jump: $('#st-jump'),
      chips: $('#st-chips'), count: $('[data-count]'), unit: $('[data-unit]'), total: $('[data-total]'), totalWrap: $('[data-total-wrap]'), live: $('#st-live'), filters: $('#st-filters'),
      fbody: $('[data-filters-body]'), views: $$('input[name="st-view"]'), viewType: $('[data-view-opt="type"]'), sbarN: $('[data-sbar-n]'), sbarNvh: $('[data-sbar-n-vh]'), sbarCount: $('[data-sbar-count]'), sbarUnit: $('[data-sbar-unit]'),
      sbarClear: $('[data-sbar-clear]'), open: $('[data-sheet-open]'), done: $('[data-sheet-done]'), scrim: $('.fscrim'),
      top: $('#st-results-top')
    };
    var env, ui, all = [], present = dict(), hasOnline = false, typeLabels, typeChoice, typeUsesRecords = dict();
    var state = { q: '', areas: dict(), types: dict(), view: 'region' }, expanded = {}, reveal = null, lastSearch = null, lastLive = null, firstRender = true;

    function fail() { host.removeAttribute('data-ready'); host.querySelector('[data-load-error]').hidden = false; }

    try {
      var model = JSON.parse(host.querySelector('[data-vocabulary]').textContent);
      model.stores = cards.map(record).sort(function(a, b) { return a.sort - b.sort || byName(a, b); });
      env = makeEnv(model);
      ui = env.ui;
      all = prep(env, model.stores);
    } catch (err) { console.error(err); fail(); return; }

    /* ----- model ----- */
    function dataPrefs(r) { return r.prefs.map(function (p) { return p.ja; }).filter(function (p) { return present[p]; }); }
    function areaOn() { return Object.keys(state.areas).length > 0; }
    function matches(s, o) {
      var i;
      for (i = 0; i < o.q.length; i++) if (!termMatches(s, o.q[i])) return false;
      if (s.channel !== CATALOG && o.areas && Object.keys(o.areas).length && !(s.channel === 'online' ? o.areas[ONLINE] : o.areas[s.pref])) return false;
      if (o.types && Object.keys(o.types).length && !o.types[s.type]) return false;
      return true;
    }
    function current() {
      var o = { q: terms(state.q), areas: state.areas, types: state.types };
      return all.filter(function (s) { return matches(s, o); });
    }
    function hasQuery() { return terms(state.q).length > 0; }
    function filtersActive() { return hasQuery() || areaOn() || Object.keys(state.types).length > 0; }
    function fullRegions() { return env.model.regions.filter(function (r) { var ps = dataPrefs(r); return ps.length && ps.every(function (v) { return state.areas[v]; }); }); }

    /* ----- URL <-> state ----- */
    function readURL() {
      var p = new URLSearchParams(location.search);
      state.q = (p.get('q') || '').slice(0, 100);
      state.areas = dict(); state.types = dict();
      p.getAll('prefecture').forEach(function (v) { if (present[v]) state.areas[v] = true; });
      p.getAll('region').forEach(function (k) {
        if (k === ONLINE) { if (hasOnline) state.areas[ONLINE] = true; }
        else if (env.regionByKey[k]) dataPrefs(env.regionByKey[k]).forEach(function (v) { state.areas[v] = true; });
      });
      if (typeChoice) p.getAll('type').forEach(function (v) { if (env.typeBy[v]) state.types[v] = true; });
      state.view = VIEWS.indexOf(p.get('grouping')) >= 0 && (p.get('grouping') !== 'type' || typeChoice) ? p.get('grouping') : 'region';
    }
    function buildURL() {
      var p = new URLSearchParams(location.search), covered = {};
      ['q', 'region', 'prefecture', 'type', 'grouping'].forEach(function(key) { p.delete(key); });
      if (hasQuery()) p.set('q', state.q.trim());
      fullRegions().forEach(function (r) { p.append('region', r.key); dataPrefs(r).forEach(function (v) { covered[v] = true; }); });
      Object.keys(state.areas).forEach(function (v) { if (v !== ONLINE && !covered[v]) p.append('prefecture', v); });
      if (state.areas[ONLINE]) p.append('region', ONLINE);
      Object.keys(state.types).forEach(function (v) { p.append('type', v); });
      if (state.view !== 'region') p.set('grouping', state.view);
      var s = p.toString();
      return location.pathname + (s ? '?' + s : '');
    }
    function syncURL(push) {
      var u = buildURL();
      if (u === location.pathname + location.search) return;
      history[push ? 'pushState' : 'replaceState'](null, '', u);
    }

    /* ----- filter panel (built once, then updated in place) ----- */
    function opt(attr, val, label) {
      return '<label class="fopt"><input type="checkbox" ' + attr + '="' + esc(val) + '" data-label="' + esc(label) + '"><span class="fopt__t">' + esc(label) + '</span><em aria-hidden="true" data-n-' + attr.replace('data-', '') + '="' + esc(val) + '"></em></label>';
    }
    function buildFilters() {
      var area = '', types = '';
      env.model.regions.forEach(function (r) {
        var ps = dataPrefs(r); if (!ps.length) return;
        area += '<div class="freg" data-region="' + r.key + '"><div class="freg__row">' + opt('data-region-input', r.key, label(r)) +
          (ps.length > 1 ? '<button type="button" class="freg__toggle" aria-expanded="false" aria-controls="fp-' + r.key + '"><span class="visually-hidden">' + esc(format(ui.prefectures, { region: label(r) })) + '</span>' + icon('down') + '</button>' : '') + '</div>' +
          (ps.length > 1 ? '<ul class="freg__prefs" id="fp-' + r.key + '" hidden>' + ps.map(function (p) { return '<li>' + opt('data-pref-input', p, env.prefInfo[p].label) + '</li>'; }).join('') + '</ul>' : '') + '</div>';
      });
      if (hasOnline) area += '<div class="freg freg--online"><div class="freg__row">' + opt('data-online-input', ONLINE, env.onlineLabel) + '</div></div>';
      if (typeChoice) {
        var seen = dict(); all.forEach(function (s) { seen[s.type] = true; });
        env.model.types.forEach(function (t) { if (seen[t.key]) types += opt('data-type-input', t.key, t.ja); });
      }
      el.fbody.innerHTML = '<div class="fgroup" role="group" aria-labelledby="fg-area"><p class="fgroup__t" id="fg-area">' + esc(ui.area) + '</p>' + area + '</div>' +
        (typeChoice ? '<div class="fgroup" role="group" aria-labelledby="fg-type"><p class="fgroup__t" id="fg-type">' + esc(ui.type) + '</p>' + types + '</div>' : '');
      el.fbody.innerHTML = uniqueMarkup(el.fbody.innerHTML);
      // few areas: show prefectures straight away; many: keep folded
      var few = Object.keys(present).length <= 12;
      $$('.freg', el.fbody).forEach(function (reg) { if (few) setOpen(reg, true); });
    }
    function setOpen(reg, open) {
      var b = $('.freg__toggle', reg); if (!b) return; b.setAttribute('aria-expanded', open ? 'true' : 'false');
      $('.freg__prefs', reg).hidden = !open; reg.classList.toggle('is-open', open);
    }
    function updateFilters() {
      var qTerms = terms(state.q), pc = dict(), tc = dict(), oc = 0;
      all.forEach(function (s) {
        if (matches(s, { q: qTerms, types: state.types })) { if (s.channel === 'online') oc++; else if (s.channel === 'physical') pc[s.pref] = (pc[s.pref] || 0) + 1; }
        if (matches(s, { q: qTerms, areas: state.areas })) tc[s.type] = (tc[s.type] || 0) + 1;
      });
      // A control that has focus is never disabled under the user's hands (unchecking a now-empty option keeps focus on it).
      function dim(input, n, unit) {
        var off = !n && !input.checked;
        input.disabled = off && input !== document.activeElement;
        input.closest('.fopt').classList.toggle('is-zero', off);
        input.setAttribute('aria-label', input.getAttribute('data-label') + ' ' + n + unit);
      }
      function put(sel, n) { var e = $(sel, el.fbody); if (e) e.textContent = n; }
      $$('[data-pref-input]', el.fbody).forEach(function (i) { var p = i.getAttribute('data-pref-input'), n = pc[p] || 0; i.checked = !!state.areas[p]; put('[data-n-pref-input="' + p + '"]', n); dim(i, n, ui.store_unit); });
      $$('[data-type-input]', el.fbody).forEach(function (i) { var t = i.getAttribute('data-type-input'), n = tc[t] || 0; i.checked = !!state.types[t]; put('[data-n-type-input="' + t + '"]', n); dim(i, n, typeUsesRecords[t] ? ui.record_unit : ui.store_unit); });
      $$('[data-online-input]', el.fbody).forEach(function (i) { i.checked = !!state.areas[ONLINE]; put('[data-n-online-input]', oc); dim(i, oc, ui.record_unit); });
      $$('[data-region-input]', el.fbody).forEach(function (i) {
        var k = i.getAttribute('data-region-input'), ps = dataPrefs(env.regionByKey[k]), sel = ps.filter(function (p) { return state.areas[p]; }).length, n = 0;
        ps.forEach(function (p) { n += pc[p] || 0; });
        i.checked = sel === ps.length; i.indeterminate = sel > 0 && sel < ps.length;
        put('[data-n-region-input="' + k + '"]', n); dim(i, n, ui.store_unit);
        var reg = $('.freg[data-region="' + k + '"]', el.fbody);
        if (sel && !reg.classList.contains('is-open')) setOpen(reg, true);
      });
      $$('[data-quick-area]', el.quick).forEach(function (b) { b.setAttribute('aria-pressed', state.areas[b.getAttribute('data-quick-area')] ? 'true' : 'false'); });
    }

    /* ----- quick lane: top prefectures, with the online shortcut third. On wide screens only the chips that fit are shown. ----- */
    function buildQuick() {
      var cnt = dict(); all.forEach(function (s) { if (s.channel === 'physical') cnt[s.pref] = (cnt[s.pref] || 0) + 1; });
      var top = Object.keys(cnt).sort(function (a, b) { return cnt[b] - cnt[a] || env.prefInfo[a].order - env.prefInfo[b].order; }).slice(0, 6);
      var oc = all.filter(function (s) { return s.channel === 'online'; }).length;
      var items = top.map(function (p) { return { v: p, n: cnt[p], label: env.prefInfo[p].label + ' ' + cnt[p] + ui.store_unit, text: env.prefInfo[p].label }; });
      if (oc) items.splice(Math.min(3, items.length), 0, { v: ONLINE, n: oc, label: env.onlineLabel + ' ' + oc + ui.record_unit, text: env.onlineLabel });
      el.quick.innerHTML = items.map(function (it) {
        return '<li' + (it.v === ONLINE ? ' data-q-online' : '') + '><button type="button" class="qpick" data-quick-area="' + esc(it.v) + '" aria-pressed="false" aria-label="' + esc(it.label) + '">' + esc(it.text) + '<span aria-hidden="true">' + it.n + '</span></button></li>';
      }).join('');
    }
    var narrow = window.matchMedia('(max-width: 768px)');
    function fitQuick() {
      var lis = $$('li', el.quick); lis.forEach(function (li) { li.hidden = false; });
      if (narrow.matches || !lis.length) return; // phones scroll the lane sideways
      var avail = el.quick.clientWidth, gap = 8, used = 0, online = $('[data-q-online]', el.quick);
      if (online) used = online.offsetWidth + gap;
      lis.forEach(function (li) {
        if (li === online) return;
        var w = li.offsetWidth + gap;
        if (used + w <= avail + gap) used += w; else li.hidden = true;
      });
    }

    /* ----- chips ----- */
    function chipList() {
      var chips = [], covered = {};
      if (hasQuery()) chips.push({ k: 'q', label: format(ui.query, { query: state.q.trim() }), aria: format(ui.keyword, { query: state.q.trim() }) });
      fullRegions().forEach(function (r) { chips.push({ k: 'region:' + r.key, label: label(r), aria: label(r) }); dataPrefs(r).forEach(function (v) { covered[v] = true; }); });
      Object.keys(state.areas).filter(function (p) { return p !== ONLINE && !covered[p]; }).sort(function (a, b) { return env.prefInfo[a].order - env.prefInfo[b].order; })
        .forEach(function (p) { chips.push({ k: 'pref:' + p, label: env.prefInfo[p].label, aria: env.prefInfo[p].label }); });
      if (state.areas[ONLINE]) chips.push({ k: 'pref:' + ONLINE, label: env.onlineLabel, aria: env.onlineLabel });
      env.model.types.forEach(function (t) { if (state.types[t.key]) chips.push({ k: 'type:' + t.key, label: t.ja, aria: t.ja }); });
      return chips.filter(function (c) { return String(c.label == null ? '' : c.label).trim(); });
    }
    function removeChip(k) {
      var i = k.indexOf(':'), kind = i < 0 ? k : k.slice(0, i), v = k.slice(i + 1);
      if (kind === 'q') state.q = ''; else if (kind === 'pref') delete state.areas[v]; else if (kind === 'type') delete state.types[v];
      else if (kind === 'region') dataPrefs(env.regionByKey[v]).forEach(function (p) { delete state.areas[p]; });
    }
    function renderChips(chips) {
      el.chips.hidden = !chips.length;
      // On phones the search field already shows the keyword, so its chip is not repeated (and a keyword-only row is dropped)
      el.chips.classList.toggle('is-q-only', chips.length === 1 && chips[0].k === 'q');
      el.chips.innerHTML = chips.length ? '<ul class="chips" aria-label="' + esc(ui.active_filters) + '">' + chips.map(function (c) {
        return '<li' + (c.k === 'q' ? ' class="st-chip-q"' : '') + '><button type="button" class="chip" data-chip="' + esc(c.k) + '" aria-label="' + esc(format(ui.remove_filter, { label: c.aria })) + '">' + esc(c.label) + icon('x') + '</button></li>';
      }).join('') + '<li><button type="button" class="chip-reset" data-reset>' + esc(ui.reset) + '</button></li></ul>' : '';
    }

    /* ----- render ----- */
    var liveTimer;
    function announce(msg) { if (msg === lastLive) return; lastLive = msg; el.live.textContent = msg; }
    function render() {
      var list = current(), n = list.length, chips = chipList(), act = filtersActive(), unit = countUnit(list, ui);
      var ctx = { expanded: expanded, limited: n > LIMIT_TOTAL, reveal: reveal, typeLabels: typeLabels };
      var res = renderResults(env, list, state.view, ctx);
      el.results.innerHTML = uniqueMarkup(res.html);
      el.jump.innerHTML = uniqueMarkup(jumpHTML(state.view, res.groups, n, ui));
      el.results.hidden = !n; el.empty.hidden = !!n;
      el.count.textContent = n; el.unit.textContent = unit; el.sbarCount.textContent = n; el.sbarUnit.textContent = unit;
      el.totalWrap.hidden = !act;
      renderChips(chips);
      el.sbarN.textContent = chips.length; el.sbarN.hidden = !chips.length; el.sbarNvh.textContent = chips.length ? format(ui.applied, { count: chips.length }) : '';
      el.sbarClear.hidden = !chips.length;
      el.done.textContent = n ? format(ui.view_results, { count: n + unit }) : ui.no_results;
      $$('[data-reset-btn]').forEach(function (b) { b.hidden = !act; });
      el.clearQ.hidden = !state.q;
      el.views.forEach(function (v) { v.checked = v.value === state.view; });
      updateFilters();
      doc.classList.remove('st-pending');
      lastSearch = location.search;
      var msg = n ? format(ui.found, { count: n + unit }) : ui.no_results;
      clearTimeout(liveTimer);
      if (firstRender) { lastLive = msg; firstRender = false; } // the page load itself is not announced
      else liveTimer = setTimeout(function () { announce(msg); }, 300);
      if (reveal) { var r = $('[data-reveal] .st-card__name', el.results); if (r) r.focus({ preventScroll: false }); reveal = null; }
    }
    function apply(push) { syncURL(push); render(); }

    /* ----- events ----- */
    var composing = false, qTimer;
    function onQuery() { state.q = el.input.value; clearTimeout(qTimer); qTimer = setTimeout(function () { apply(false); }, 120); }
    function reset(focusEl) {
      state.q = ''; state.areas = dict(); state.types = dict(); el.input.value = '';
      apply(true); if (focusEl) focusEl.focus();
    }
    function toggleArea(v) { if (state.areas[v]) delete state.areas[v]; else state.areas[v] = true; }
    function init() {
      all.forEach(function (s) { if (s.channel === 'online') hasOnline = true; else if (s.channel === 'physical' && s.pref) present[s.pref] = true; });
      all.forEach(function (s) { if (s.channel === 'online' || s.channel === CATALOG) typeUsesRecords[s.type] = true; });
      typeLabels = typeLabelFlags(all);
      typeChoice = hasTypeChoice(all);
      if (el.viewType) el.viewType.hidden = !typeChoice;
      el.total.textContent = all.length;
      $('[data-total-unit]').textContent = countUnit(all, ui);
      buildQuick();
      buildFilters();
      readURL(); el.input.value = state.q;
      render();
      fitQuick();
      if (window.ResizeObserver) (host.resizeObserver = new ResizeObserver(fitQuick)).observe(el.quick); else on(window, 'resize', fitQuick);

      on(el.input, 'input', function () { if (!composing) onQuery(); });
      on(el.input, 'compositionstart', function () { composing = true; });
      on(el.input, 'compositionend', function () { composing = false; onQuery(); });
      on($('.st-search'), 'submit', function (e) {
        e.preventDefault(); clearTimeout(qTimer); state.q = el.input.value; apply(false);
        // Touch: dismiss the keyboard and bring the results up. Keyboard users stay in the field.
        if (coarse.matches) { el.input.blur(); scrollToResults(); }
      });
      on(el.clearQ, 'click', function () { el.input.value = ''; state.q = ''; apply(true); el.input.focus(); });

      on(el.quick, 'click', function (e) {
        var b = e.target.closest('[data-quick-area]'); if (!b) return;
        toggleArea(b.getAttribute('data-quick-area')); apply(true);
      });
      on(el.fbody, 'change', function (e) {
        var t = e.target, v;
        if ((v = t.getAttribute('data-pref-input')) !== null) { if (t.checked) state.areas[v] = true; else delete state.areas[v]; }
        else if ((v = t.getAttribute('data-online-input')) !== null) { if (t.checked) state.areas[ONLINE] = true; else delete state.areas[ONLINE]; }
        else if ((v = t.getAttribute('data-type-input')) !== null) { if (t.checked) state.types[v] = true; else delete state.types[v]; }
        else if ((v = t.getAttribute('data-region-input')) !== null) { dataPrefs(env.regionByKey[v]).forEach(function (p) { if (t.checked) state.areas[p] = true; else delete state.areas[p]; }); }
        else return;
        apply(true);
      });
      on(el.fbody, 'click', function (e) {
        var b = e.target.closest('.freg__toggle'); if (!b) return;
        var reg = b.closest('.freg'); setOpen(reg, b.getAttribute('aria-expanded') !== 'true');
      });
      el.views.forEach(function (v) { on(v, 'change', function () { if (v.checked) { state.view = v.value; apply(false); } }); });
      on(finder, 'click', function (e) {
        var c = e.target.closest('[data-chip]'), m = e.target.closest('[data-more]'), r = e.target.closest('[data-reset]'), j = e.target.closest('.st-jump a');
        if (j) { e.preventDefault(); jumpTo(j.getAttribute('href').slice(1)); }
        if (c) {
          var idx = $$('[data-chip]', el.chips).indexOf(c);
          removeChip(c.getAttribute('data-chip')); apply(true);
          var left = $$('[data-chip]', el.chips);
          (left.length ? left[Math.min(idx, left.length - 1)] : el.input).focus();
        }
        if (m) { var k = m.getAttribute('data-more'); expanded[k] = true; reveal = k; render(); }
        if (r) reset(el.input);
      });
      // The reset buttons hide themselves once nothing is active, so focus moves to a control that stays: the sheet heading inside the open sheet, the search box on the page.
      $$('[data-reset-btn]').forEach(function (b) { on(b, 'click', function () { reset(sheetOpen ? $('#st-filters-h') : el.input); }); });
      on(el.sbarClear, 'click', function () { reset(el.open); });
      on(window, 'popstate', function () { if (location.search === lastSearch) return; readURL(); el.input.value = state.q; render(); });

      initSheet();
      host.setAttribute('data-ready', '');
      host.querySelector('[data-pages]').hidden = true;
    }
    function scrollToResults() { el.top.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' }); }
    function jumpTo(id) {
      var g = document.getElementById(id); if (!g) return;
      g.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      var h = $('.st-group__h', g); if (h) h.focus({ preventScroll: true });
    }

    /* ----- mobile bottom sheet ----- */
    var mq = window.matchMedia('(max-width: 900px)'), sheetOpen = false, lastFocus = null;
    var INERT = '[data-section="announcement-bar"], [data-section="site-header"], [data-section="breadcrumbs"], .st-hero, .st-finder__main, .sbar, .st-b2b, [data-section="service-links"], [data-section="site-footer"]';
    function syncMode() {
      if (mq.matches) { el.filters.setAttribute('role', 'dialog'); el.filters.setAttribute('aria-modal', 'true'); el.filters.setAttribute('aria-labelledby', 'st-filters-h' + suffix); }
      else { el.filters.setAttribute('role', 'group'); el.filters.removeAttribute('aria-modal'); el.filters.removeAttribute('aria-labelledby'); if (sheetOpen) setSheet(false, true); }
    }
    function setSheet(open, silent) {
      if (open === sheetOpen) return;
      sheetOpen = open;
      el.filters.classList.toggle('is-open', open); el.scrim.classList.toggle('is-open', open);
      doc.classList.toggle('st-lock', open);
      el.open.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.querySelectorAll(INERT).forEach(function (n) { if (open) { n.dataset.stockistsWasInert = n.inert ? '1' : '0'; n.inert = true; } else { n.inert = n.dataset.stockistsWasInert === '1'; delete n.dataset.stockistsWasInert; } });
      el.filters.style.transform = '';
      if (open) { lastFocus = document.activeElement; setTimeout(function () { $('#st-filters-h').focus(); }, reduced ? 0 : 60); }
      else if (!silent && lastFocus) lastFocus.focus();
    }
    function initSheet() {
      syncMode(); on(mq, 'change', syncMode);
      on(el.open, 'click', function () { setSheet(true); });
      $$('[data-sheet-close]').forEach(function (b) { on(b, 'click', function () { setSheet(false); }); });
      on(el.scrim, 'click', function () { setSheet(false); });
      on(el.done, 'click', function () { setSheet(false); scrollToResults(); });
      on(document, 'keydown', function (e) {
        if (!sheetOpen) return;
        if (e.key === 'Escape') { e.preventDefault(); setSheet(false); return; }
        if (e.key !== 'Tab') return;
        var f = $$('button:not([hidden]):not([disabled]), input:not([disabled]), a[href], h2[tabindex]', el.filters).filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === $('#st-filters-h'))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      // drag the handle area down to dismiss
      var head = $('.filters__head'), y0 = null, dy = 0;
      on(head, 'pointerdown', function (e) { if (!mq.matches || e.target.closest('button')) return; y0 = e.clientY; dy = 0; head.setPointerCapture(e.pointerId); el.filters.style.transition = 'none'; });
      on(head, 'pointermove', function (e) { if (y0 === null) return; dy = Math.max(0, e.clientY - y0); el.filters.style.transform = 'translateY(' + dy + 'px)'; });
      function endDrag() { if (y0 === null) return; y0 = null; el.filters.style.transition = ''; if (dy > 90) setSheet(false); else el.filters.style.transform = ''; }
      on(head, 'pointerup', endDrag); on(head, 'pointercancel', endDrag);
    }
    host.cleanup = function() { clearTimeout(liveTimer); clearTimeout(qTimer); host.resizeObserver?.disconnect(); if (sheetOpen) setSheet(false, true); };
    try { init(); } catch (error) { console.error(error); host.cleanup(); host.controller.abort(); fail(); }
  }

  return { makeEnv: makeEnv, prep: prep, renderResults: renderResults, jumpHTML: jumpHTML, typeLabelFlags: typeLabelFlags, hasTypeChoice: hasTypeChoice, countUnit: countUnit, countText: countText, termMatches: termMatches, terms: terms, norm: norm, boot: boot };
});
